import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

import { PRODUCT_NAME, PRODUCT_SHORT, PRODUCT_TAGLINE, BRAND_LOGO } from '../lib/version';

// Default branding (NecroMancer V3). The logo is the Spring server's own mark (public/brand/).
const DEFAULT_BRANDING = {
  appName: PRODUCT_NAME,
  appShortName: PRODUCT_SHORT,
  tagline: PRODUCT_TAGLINE,
  logoUrl: BRAND_LOGO,
  faviconUrl: '/favicon.ico',
  theme: 'dark-cyan',
  colors: {
    primary: '#3dd0e2',
    secondary: '#0e87fb',
    accent: '#f59e0b',
    background: '#05070d',
    foreground: '#eef2f8'
  },
  fonts: {
    heading: 'Space Grotesk',
    body: 'Manrope'
  },
  customCss: null,
  planFeatures: {
    preset_themes: true,
    color_picker: false,
    custom_css: false,
    custom_fonts: false,
    api_access: false
  }
};

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  tenantId: null,
  isLoading: true,
  error: null,
  refreshBranding: () => {},
  applyTheme: () => {}
});

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [tenantId, setTenantId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch branding on mount
  useEffect(() => {
    fetchBranding();
  }, []);

  // Apply theme whenever branding changes
  useEffect(() => {
    if (branding) {
      applyTheme(branding);
    }
  }, [branding]);

  const fetchBranding = async () => {
    // Tenant branding was a V2 (FastAPI) feature. The Spring server has no /api/tenants/branding
    // endpoint, so only ask when a separate backend is configured - otherwise every page load
    // logged a failed request and the defaults above are the answer anyway.
    if (!process.env.REACT_APP_BACKEND_URL) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);

      // Get current domain for tenant detection
      const currentDomain = window.location.hostname;
      
      const API_URL = process.env.REACT_APP_BACKEND_URL 
        ? `${process.env.REACT_APP_BACKEND_URL}/api` 
        : '/api';

      const response = await axios.get(`${API_URL}/tenants/branding/public`, {
        params: { domain: currentDomain }
      });

      if (response.data) {
        const data = response.data;
        
        setBranding({
          appName: data.app_name || DEFAULT_BRANDING.appName,
          appShortName: data.app_short_name || DEFAULT_BRANDING.appShortName,
          tagline: data.tagline || DEFAULT_BRANDING.tagline,
          logoUrl: data.logo_url,
          faviconUrl: data.favicon_url,
          theme: data.theme || DEFAULT_BRANDING.theme,
          colors: data.colors || DEFAULT_BRANDING.colors,
          fonts: data.fonts || DEFAULT_BRANDING.fonts,
          customCss: data.custom_css,
          planFeatures: data.plan_features || DEFAULT_BRANDING.planFeatures
        });
        
        setTenantId(data.tenant_id);

        // Update document title
        document.title = data.app_name || DEFAULT_BRANDING.appName;

        // Update favicon if provided
        if (data.favicon_url) {
          const favicon = document.querySelector('link[rel="icon"]');
          if (favicon) {
            favicon.href = data.favicon_url;
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch branding:', err);
      setError(err.message);
      // Keep default branding on error
    } finally {
      setIsLoading(false);
    }
  };

  const applyTheme = (brandingConfig) => {
    const root = document.documentElement;
    const colors = brandingConfig.colors || {};

    // Apply CSS custom properties
    if (colors.primary) {
      root.style.setProperty('--color-primary', colors.primary);
      root.style.setProperty('--tenant-primary', colors.primary);
    }
    if (colors.secondary) {
      root.style.setProperty('--color-secondary', colors.secondary);
      root.style.setProperty('--tenant-secondary', colors.secondary);
    }
    if (colors.accent) {
      root.style.setProperty('--color-accent', colors.accent);
      root.style.setProperty('--tenant-accent', colors.accent);
    }
    if (colors.background) {
      root.style.setProperty('--color-background', colors.background);
      root.style.setProperty('--tenant-background', colors.background);
    }
    if (colors.foreground) {
      root.style.setProperty('--color-foreground', colors.foreground);
      root.style.setProperty('--tenant-foreground', colors.foreground);
    }

    // Apply custom fonts if enterprise plan
    const fonts = brandingConfig.fonts || {};
    if (fonts.heading) {
      root.style.setProperty('--font-heading', fonts.heading);
    }
    if (fonts.body) {
      root.style.setProperty('--font-body', fonts.body);
    }

    // Apply custom CSS if enterprise plan
    if (brandingConfig.customCss) {
      let customStyleEl = document.getElementById('tenant-custom-css');
      if (!customStyleEl) {
        customStyleEl = document.createElement('style');
        customStyleEl.id = 'tenant-custom-css';
        document.head.appendChild(customStyleEl);
      }
      customStyleEl.textContent = brandingConfig.customCss;
    }
  };

  const value = {
    branding,
    tenantId,
    isLoading,
    error,
    refreshBranding: fetchBranding,
    applyTheme: () => applyTheme(branding)
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

// Hook for consuming branding context
export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}

// Utility hook for getting specific branding values
export function useAppName() {
  const { branding } = useBranding();
  return branding.appName;
}

export function useLogo() {
  const { branding } = useBranding();
  return branding.logoUrl;
}

export function useTheme() {
  const { branding } = useBranding();
  return {
    theme: branding.theme,
    colors: branding.colors
  };
}

export default BrandingContext;
